import BaseViewService from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-service';
import { IMobileToolbarProps, IMobileToolbarService } from '@src/slide/application/service/mobile-tool-bar/core/type';
import MobileToolBarModel from '@src/slide/application/service/mobile-tool-bar/core/mobile-toolbar-model';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import { IMobileToolbarInjectable } from '../inject/mobile-toolbar-injectable.interface';
import { SoftKeyboard } from '@tencent/docs-soft-keyboard';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { network } from '@tencent/netcheck';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { isIpadExternalKeyboardOpen, softKeyboardHeightManager } from '@com-m/utils/soft-keyboard-height-manager';
import _debounce from 'lodash/debounce';
import {
    IItemSelectedState,
    IMobileToolBarEditServiceDelegates,
    IMobileToolBarServiceDelegates,
    IMobileToolBarServiceLogic,
    IMobileToolBarViewerServiceDelegates,
    IToolBarData,
} from '@src/slide/application/service/mobile-tool-bar/core/mobile-toolbar-service.interface';
import { isInMagicBox, topAccessibleWindow } from '@src/slide/application/service/magicbox-service/utils';
import { isPortrait } from 'utils';
import { getIosSafeBottom, getSafariBottom } from '@utils/safe-area';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { EDITOR_INPUT_CLASS_NAME } from '@tencent/alloy-editor';
import MobileToolbarDataProcessor from '@com-m/tool-bar-m/menu/toolbar-data-processor';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { isSupportNativeTitleBar } from '@src/js/manager/native-titlebar-manager';
import { MOBILE_BOTTOM_HEIGHT, MOBILE_HEAD_HEIGHT } from '@src/js/constant';
import { ColorPanelType, EditPanelType, getMobileEditPanelHeight } from '@com-m/edit-panel-m/const';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import mobileToolBarHandlerMap from '@com-m/tool-bar-m/tool-bar-handler-m';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import {
    ModelMenuRefreshEventsKeys,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';
import { isWebOffice } from '@tencent/docs-connection';
import { DOMId } from '@editor/src/page/index/components/canvas/canvas-constants';

const IpadExternalKeyboardHeight = 69;
const isNeedAutoScrollOnFocus = browser.isIOS;

/**
 * 移动端toolbar react组件抽出的View Service
 */
export default class MobileToolbarService extends BaseViewService<IMobileToolbarProps>
    implements IMobileToolbarService {
    declare protected model: MobileToolBarModel;

    // service
    private services?: IMobileToolBarServiceDelegates;

    private softKeyboard: SoftKeyboard;
    private stopIPadExternalKeyboardChange: boolean;
    // 延迟更新ui，避免页面跳动，因为组合的addRefreshFunc 30ms才更新一次汇总计算
    private delayUpdateUi = _debounce(() => {
        this.updateModel<MobileToolBarModel>({ finishSave: this.model.finishSave + 1 });
    }, 50, { leading: false, trailing: true });

    // CANVAS Dom
    private canvasDom: HTMLCanvasElement | null = null;
    private canvasDomPreTop = '0';

    public constructor(private injectable?: IMobileToolbarInjectable) {
        super();

        this.softKeyboard = new SoftKeyboard();
        this.stopIPadExternalKeyboardChange = false;

        // 部分入口置灰，需要关联在线状态，app事件随组件回收，window事件需要主动回收
        this.registerEvent();
    }

    public setViewerServiceDelegates(services: IMobileToolBarViewerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public setEditServiceDelegates(services: IMobileToolBarEditServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public shouldRenderToolBar(): boolean {
        if (this.services?.noteAreaManager?.isMobileNoteFullScreen) {
            return false;
        }
        const shouldRender = this.injectable?.shouldRenderToolBar() ?? true;
        if (!shouldRender) {
            return false;
        }
        return true;
    }

    public getToolBarData(): IToolBarData {
        const toolbarData = { items: MobileToolbarDataProcessor.getGroupListByType(this.model.isKeyBoardOn) };
        const editPanelData = { items: MobileToolbarDataProcessor.getGroupListWithTextEditPanel() };
        const insertItemData = { items: MobileToolbarDataProcessor.getInsertList() };
        const addonItemData = { items: MobileToolbarDataProcessor.getAddonList() };
        if (this.isNeedRemoveUndoRedo()) {
            toolbarData.items = toolbarData.items.filter(item => !(
                [ActionType.UNDO, ActionType.REDO].includes(item.id)));
        }
        return {
            toolbarData, editPanelData, insertItemData, addonItemData,
        };
    }

    public getPanelType(): { editPanelType: string; colorPanelType: ColorPanelType; } {
        const {
            colorPanelType = ColorPanelType.NONE,
            editPanelType = '',
        } = this.services?.mobileToolbarEditManager || {};
        return { colorPanelType, editPanelType };
    }

    public getToolbarExtraStyle(): ReturnType<IMobileToolBarServiceLogic['getToolbarExtraStyle']> {
        const { editPanelShowState } = this.services?.mobileToolbarEditManager || {};
        let height = MOBILE_BOTTOM_HEIGHT;
        if (editPanelShowState) {
            height = getMobileEditPanelHeight() + MOBILE_BOTTOM_HEIGHT + getSafariBottom();
        }

        const style: ReturnType<IMobileToolBarServiceLogic['getToolbarExtraStyle']> = { height: `${height}px` };

        if (browser.isMobile && isWebOffice) {
            if (this.model.isEditing) {
                style.top = `${this.model.panelTop}px`;
                style.bottom = 'auto';
            }
        }
        return style;
    }

    public getToolbarBottom() {
        let bottom;
        if (this.model.isKeyBoardOn && !this.props?.isAndroidKeyboardOn) {
            if (this.model.keyBoardHeight === 0) {
                bottom = getIosSafeBottom();
            } else {
                bottom = this.model.panelBottom;
            }
        } else {
            bottom = this.model.initialBottom;
        }
        if (this.services?.mobileToolbarEditManager?.editPanelShowState) {
            // canvas 版本中文字编辑面板是工具栏的子元素，所以在编辑面板打开时 bottom 为 0
            bottom = 0;
        }
        return bottom > 0 ? bottom : 0; // qq h5端有小于0的场景，导致定位在页面之外
    }

    public setToolbarElement(el) {
        el && this.services?.mobileToolbarEditManager?.setToolbarElement(el);
    }

    // 插入图片按钮是否可用
    public isInsertPicBtnEnable(): boolean {
        const isNoteFullScreen = this.services?.noteAreaManager?.isMobileNoteFullScreen;
        if (isNoteFullScreen) {
            return false;
        }
        const injectState = this.injectable?.isInsertPicBtnEnable();
        if (typeof injectState !== 'undefined') {
            return injectState;
        }
        if (!this.model.isOnline) {
            return false;
        }
        return true;
    }

    public getItemSelectedState(): IItemSelectedState {
        const note = !!this.services?.noteAreaManager?.show;
        const textEdit = !!this.isEditPanelSelected(EditPanelType.TEXT_EDIT);
        const addEdit = !!this.isEditPanelSelected(EditPanelType.ADD_ADIT);
        const layoutSuggestion = !!this.isEditPanelSelected(EditPanelType.LAYOUT_SUGGESTION);
        const replaceSlideAI = !!this.isEditPanelSelected(EditPanelType.AI_REPLACE_SLIDE);
        return { note, textEdit, addEdit, layoutSuggestion, replaceSlideAI };
    }

    /* eslint-disable complexity */
    public handleImageChange = (imgs) => {
        const selectionService = this.services?.selectionService;
        const uiDataService = this.services?.uiDataService;

        if (!selectionService?.currentSlide) return;

        const { currentSlideIndex, slides } = selectionService;
        const { presentation } = uiDataService || {};

        if (!imgs || imgs.length === 0) {
            return;
        }

        const types: Array<UIModelType> = [];
        for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i];
            const width = ptToCanvas(img.width);
            const height = ptToCanvas(img.height);
            const { slideWidth = 0, slideHeight = 0 } = presentation?.props || {};

            let nextWidth = width;
            let nextHeight = height;
            if (width > slideWidth || height > slideHeight) {
                const ratioX = slideWidth / width;
                const ratioY = slideHeight / height;
                const ratio = ratioX < ratioY ? ratioX : ratioY;
                nextWidth = width * ratio;
                nextHeight = height * ratio;
            }

            const left = (slideWidth - nextWidth) / 2;
            const top = (slideHeight - nextHeight) / 2;

            img.style = {
                position: 'absolute',
                left,
                top,
                width: nextWidth,
                height: nextHeight,
                bgShape: ShapeType.rect,
                opacity: 1,
            };
            types.push(UIModelType.IMAGE);
        }

        const slideModel = slides[currentSlideIndex];
        slideModel.addElement(types, imgs);
        selectionService.lastMouseDownRegion = RegionType.EDITOR;
        selectionService.selectedSlides.clear();
        // 插入图片要关闭加号面板
        if (this.services?.mobileToolbarEditManager?.editPanelShowState) {
            this.services?.mobileToolbarEditManager.closeEditPanel();
        }
        const input = document.getElementById('select_pic_input');
        // @ts-ignore
        input.value = null;
    };

    public componentDidMount() {
        SlideDataServices.SDKDataServices.EventManager.promise()
            .then(({ mutationEvents }) => {
                // 数据更新后触发更新
                mutationEvents.onCommitCommand(() => this.delayUpdateUi());
            });

        // 组合的数据汇总迟于onCommit，所以也要监听
        this.services?.menuManager?.event
            .on(ModelMenuRefreshEventsKeys.REFRESH_MENU_VALUES_VALUES, () => this.delayUpdateUi());

        window.addEventListener('enterEditing', this.handleEnterEditing);
        window.addEventListener('exitEditing', this.handleExitEditing);

        // 获取键盘高度
        /**
         * keyboard --> { isSupported, heightInViewport, heightOutOfViewport}
         * isSupported 是否支持获取键盘高度
         * heightInViewport （iOS)键盘在视口内需要主动避开的高度
         * heightOutOfViewport （Android）键盘挤占视口不需要主动避开的高度
         * */
        /**
         * 移动端 webOffice 监听不到键盘变化（why：iframe 跨域，softKeyboard 获取不到父级 window 对象）
         * 移动端 webOffice兼容处理：在 handleEnterEditing 和 handleExitEditing 中添加额外处理逻辑
         * */
        this.softKeyboard.on('change', (keyboard) => {
            if (keyboard.isSupported && !isWebOffice) {
                if (browser.isIOS) {
                    this.handleIOSKeyboardChange(keyboard);
                } else if (browser.isAndroid) {
                    if (this.isRealKeyboardEvent()) {
                        softKeyboardHeightManager.setHeight(keyboard.heightOutOfViewport);
                    }
                    this.updateModel<MobileToolBarModel>({
                        keyBoardHeight: keyboard.heightOutOfViewport,
                        panelBottom: 0,
                    });
                }
            }
        });

        window.addEventListener('orientationchange', this.handleOrientationchange);
        // 排版检查入口曝光
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.AUTO_BEAUTIFY,
            action: TDWActionType.BEAUTIFY_M_VIEW,
            ver5: 1,
        });
    }

    // eslint-disable-next-line react/no-deprecated
    public componentWillReceiveProps(nextProps) {
        // eslint-disable-next-line react/prop-types
        const { isAndroidKeyboardOn } = nextProps;
        if (browser.isAndroid) {
            this.updateModel<MobileToolBarModel>({
                isKeyBoardOn: isAndroidKeyboardOn,
            });
        }
    }

    public componentWillUnmount() {
        this.removeEvent();
    }

    public handleMouseDown(id, value) {
        this.handleTouchStart(id, value);
    }

    public handleTouchStart(id, value) {
        if (mobileToolBarHandlerMap[id]) {
            mobileToolBarHandlerMap[id].call(this);
        } else {
            // 已转移至编辑面板里操作，编辑面板上线后此分支应删除
            this.handleTouchStartDefault(id, value);
        }
    }

    protected createViewModel(): BaseViewModel<IMobileToolbarProps> {
        return new MobileToolBarModel();
    }

    protected getInitModelValue(): Partial<MobileToolBarModel> | undefined {
        return {
            isOnline: navigator.onLine && this.getSdkOnlineStatus(),
        };
    }

    private registerEvent = () => {
        SlideDataServices.SDKDataServices.EventManager.promise()
            .then(({ socketEvents }) => {
                socketEvents.onChangeToOnline(this.handleOnOffline);
                socketEvents.onChangeToOffline(this.handleOnOffline);
            });
        window.addEventListener('online', this.handleOnOffline);
        window.addEventListener('offline', this.handleOnOffline);
    };


    private removeEvent = () => {
        window.removeEventListener('enterEditing', this.handleEnterEditing);
        window.removeEventListener('exitEditing', this.handleExitEditing);
        window.removeEventListener('orientationchange', this.handleOrientationchange);
        window.removeEventListener('online', this.handleOnOffline);
        window.removeEventListener('offline', this.handleOnOffline);
        topAccessibleWindow.removeEventListener('scroll', this.handleScroll);
        topAccessibleWindow.removeEventListener('scroll', this.handleWXScroll);
    };


    private handleOnOffline = () => {
        this.updateModel<MobileToolBarModel>({
            isOnline: navigator.onLine && this.getSdkOnlineStatus(),
        });
    };

    private getSdkOnlineStatus() {
        const injectState = this.injectable?.getSdkOnlineStatus();
        if (typeof injectState !== 'undefined') {
            return injectState;
        }
        return network.isOnline;
    }

    private handleEnterEditing = () => {
        this.updateModel<MobileToolBarModel>({
            isEditing: true,
        });

        if (this.props?.isToolbarKeepAtTop) {
            this.updateModel<MobileToolBarModel>({
                isKeyBoardOn: true,
            });
            topAccessibleWindow.addEventListener('scroll', this.handleWXScroll);
            return;
        }

        if (browser.isMobile && isWebOffice) {
            this.updateModel<MobileToolBarModel>({
                isKeyBoardOn: true,
                panelTop: MOBILE_HEAD_HEIGHT,
            });
            const canvasDom = this.getCanvasDom();
            if (canvasDom) {
                this.canvasDomPreTop = canvasDom?.style?.top ?? '0';
                canvasDom.style.top = `${MOBILE_HEAD_HEIGHT}px`;
            }
        }
    };

    private handleExitEditing = () => {
        if (this.services?.editorInstanceService?.checkEditorIsEditing()) {
            this.handleEnterEditing();
            return;
        }

        this.updateModel<MobileToolBarModel>({
            isEditing: false,
        });

        if (browser.isIOS) {
            this.handleIOSExit();
        }
        if (browser.isIPad && browser.isSafari && !isPortrait()) {
            window.scrollTo(0, 0);
        }

        if (browser.isIOS && isWebOffice) {
            this.updateModel<MobileToolBarModel>({
                isKeyBoardOn: false,
            });
            const canvasDom = this.getCanvasDom();
            if (canvasDom) {
                canvasDom.style.top = this.canvasDomPreTop;
            }
        }
    };

    private handleIOSExit() {
        // ipadpro外接键盘异常，如果在退出编辑前的键盘高度为0的话，不会触发'change'事件，会导致底部工具栏没有归位，需要手动复位
        if (this.stopIPadExternalKeyboardChange && this.model.keyBoardHeight > 0) {
            this.stopIPadExternalKeyboardChange = false;
            this.handleIOSKeyboardChange({ heightInViewport: 0 });
        }

        // iOS键盘弹起会引发界面移动位置，需要滚动一下复位
        setTimeout(() => {
            const scrollHeight = this.getScrollHeight();
            window.scrollTo(0, Math.max(scrollHeight - 1, 0));
        }, 100);

        if (!browser.isIPad) {
            topAccessibleWindow.removeEventListener('scroll', this.handleScroll);
        }
        if (this.props?.isToolbarKeepAtTop) {
            topAccessibleWindow.removeEventListener('scroll', this.handleWXScroll);
        }
    }

    private handleScroll = () => {
        const scrollHeight = this.getScrollHeight();
        this.updateModel<MobileToolBarModel>({
            panelBottom: this.model.keyBoardHeight - scrollHeight,
        });
    };

    private handleWXScroll = () => {
        const maxScroll = 420;
        let scrollHeight = this.getScrollHeight();
        if (browser.isIPad && browser.isSafari && !isPortrait()) {
            scrollHeight = scrollHeight - 63;
        }
        if (scrollHeight < 0) {
            scrollHeight = -1;
        } else if (scrollHeight >= maxScroll) {
            scrollHeight = maxScroll;
        }
        if (!this.model.isEditing) {
            scrollHeight = 0;
        }

        const panelTop = scrollHeight;
        this.updateModel<MobileToolBarModel>({ panelTop });
    };

    private getScrollHeight() {
        // 在iframe中则以iframe滚动为准
        return topAccessibleWindow.document.documentElement.scrollTop
            || topAccessibleWindow.document.body.scrollTop || 0;
    }

    private handleOrientationchange = () => {
        this.updateModel<MobileToolBarModel>({
            initialBottom: getIosSafeBottom(),
        });
    };

    private handleIOSKeyboardChange(keyboard) {
        setTimeout(() => {
            const scrollHeight = this.getScrollHeight();
            const height = keyboard.heightInViewport || keyboard.heightOutOfViewport;
            softKeyboardHeightManager.setHeight(height);
            this.handleIPadExternalKeyboardChange(height);
            if (this.stopIPadExternalKeyboardChange) return;
            const panelBottom = this.hasScrolled() ? height : height - scrollHeight + getSafariBottom();
            const isEditing = this.model.isEditing || this.services?.editorInstanceService?.checkEditorIsEditing();
            // 用户未打开键盘，ios竖屏转横屏时，偶尔会触发handleIOSKeyboardChange事件，因此增加用户是否正在编辑的判断
            if (isEditing) {
                this.updateModel<MobileToolBarModel>({
                    isKeyBoardOn: true,
                    panelBottom,
                    keyBoardHeight: height,
                });
            } else {
                this.updateModel<MobileToolBarModel>({
                    keyBoardHeight: height,
                });
            }

            if (height > 0) {
                // ios键盘弹起时的事件
                if (isEditing) {
                    eventBus.emit(EventKeys.IOS_KEY_BOARD_SHOW, height);
                    // eslint-disable-next-line max-len
                    const mobileToolbarEditManager = this.services?.mobileToolbarEditManager;
                    // 当键盘打开时，确保编辑面板已经关闭（除了 ipad 外接键盘打开的情况）
                    if (mobileToolbarEditManager?.editPanelShowState && !isIpadExternalKeyboardOpen()) {
                        mobileToolbarEditManager.closeEditPanel();
                    }
                }
            } else {
                // ios键盘隐藏时的事件
                eventBus.emit(EventKeys.IOS_KEY_BOARD_HIDE, height);

                this.updateModel<MobileToolBarModel>({
                    panelBottom: 0,
                    panelTop: 0,
                    isKeyBoardOn: false,
                });
            }
        }, isInMagicBox() ? 100 : 0);

        if (!this.hasScrolled()) {
            topAccessibleWindow.addEventListener('scroll', this.handleScroll);
        }
    }

    /**
     * 外接键盘偶尔会出现编辑态获取高度异常的情况，导致页面抖动
     * @param {*} height 外接键盘高度
     * @returns 是否停止此次change事件
     */
    private handleIPadExternalKeyboardChange(height) {
        if (!config.supportEnterprise(EnterPriseType.APP_IPAD) || height > IpadExternalKeyboardHeight) return;
        if (this.services?.editorInstanceService?.checkEditorIsEditing()) {
            const scrollHeight = this.getScrollHeight();
            // ipad pro外接键盘进入编辑态异常时会滚动页面
            if (scrollHeight > 0 && scrollHeight <= IpadExternalKeyboardHeight) {
                this.handleScroll();
            }
            // 进入编辑态如果得到的键盘高度小于69，即在键盘未隐藏的情况下触发change事件
            if (height < IpadExternalKeyboardHeight) {
                this.stopIPadExternalKeyboardChange = true;
            }
        }
    }

    private hasScrolled() {
        if (isNeedAutoScrollOnFocus) {
            return false;
        }
        return (browser.isIPad && !browser.isTencentDocApp)
            || browser.isWxMiniProgram
            || browser.isQQMiniProgram
            || browser.isWeChat
            || browser.isWxWork;
    }

    private isRealKeyboardEvent = () => {
        if (!this.services?.editorInstanceService?.checkEditorIsEditing()) {
            return false;
        }
        const editorInput = document.querySelector(`.${EDITOR_INPUT_CLASS_NAME}`);
        if (document.activeElement !== editorInput) {
            return false;
        }
        return true;
    };

    private isNeedRemoveUndoRedo = () => isSupportNativeTitleBar() && !browser.isIPad;

    private isEditPanelSelected(type) {
        const toolbarEditManager = this.services?.mobileToolbarEditManager;
        return toolbarEditManager?.editPanelShowState && toolbarEditManager.editPanelType === type;
    }

    // 已转移至编辑面板里操作，编辑面板上线后此方法应删除
    private handleTouchStartDefault = (id, value) => {
        let action;
        if (id === ActionType.BOLD) {
            action = TDWActionType.M_EDIT_BOLD;
        } else if (id === ActionType.ITALIC) {
            action = TDWActionType.M_EDIT_ITALIC;
        } else if (id === ActionType.UNDERLINE) {
            action = TDWActionType.M_EDIT_UNDERLINE;
        } else if (id === ActionType.DELETELINE) {
            action = TDWActionType.M_EDIT_DELETELINE;
        } else if (id === ActionType.FONTSIZE) {
            action = TDWActionType.M_EDIT_FONTSIZE;
        } else if (id === ActionType.HORIZONTAL_ALIGN) {
            action = TDWActionType.M_EDIT_HORIZONTAL_ALIGN;
        } else if (id === ActionType.ITEMNUMBER) {
            action = TDWActionType.M_EDIT_ITEMNUMBER;
        } else if (id === ActionType.DIGITNUMBER) {
            action = TDWActionType.M_EDIT_DIGITNUMBER;
        }
        this.operateTouchStartDefault(id, value, action);
    };

    private operateTouchStartDefault = (id, value, action) => {
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.M_EDIT_FORMAT,
                action,
            });
        }
        const { callback } = this.services?.menuManager?.stylePanelMenu() || { };
        callback?.(MenuType.TOOLBAR, id, value);
    };

    private getCanvasDom() {
        if (!this.canvasDom) {
            this.canvasDom = document.getElementById(DOMId.CANVAS) as HTMLCanvasElement;
        }
        return this.canvasDom;
    }
}
